import { CartAddEvent, ThemeEvents } from '@theme/events';

class ContentCardAddToCart {
  /**
   * Each option's label as rendered in the markup, so resets restore it verbatim. Kept here rather
   * than in a data attribute because the cart section morph strips attributes the server didn't
   * render.
   * @type {WeakMap<HTMLElement, string>}
   */
  defaultLabels = new WeakMap();

  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const target = /** @type {Element | null} */ (e.target);
      const option = target?.closest?.('.content-card__product-option');

      if (!option || !option.closest('[data-product-picker]')) {
        return;
      }

      e.preventDefault();

      if (option.hasAttribute('disabled') || option.classList.contains('loading')) {
        return;
      }

      this.handleAddToCart(/** @type {HTMLElement} */ (option));
    });
  }

  /**
   * Handle adding product to cart
   * @param {HTMLElement} optionElement
   */
  async handleAddToCart(optionElement) {
    const productId = optionElement.dataset.productId || '';
    const variantId = optionElement.dataset.variantId || '';
    const quantity = parseInt(optionElement.dataset.quantity || '1') || 1;
    const isBogo = parseInt(optionElement.dataset.bogo || '0') || 0;

    if (!variantId) {
      console.error('Variant ID not found for product option');
      return;
    }

    this.setLoadingState(optionElement, true);

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((/** @type {HTMLElement} */ item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('sections', ['header', ...cartItemComponentsSectionIds].join(','));

      let finalQuantity = quantity;
      if (isBogo && !isNaN(isBogo)) {
        finalQuantity = finalQuantity * 2;

        formData.append('properties[_bogo]', '1');
      }

      const paypalExcludeFlag = optionElement.dataset.paypalExcludeFlag;
      const itemRestrictType = optionElement.dataset.itemRestrictType;
      const amazonPayExcludeFlag = optionElement.dataset.amazonPayExcludeFlag;
      const inventoryMessage = optionElement.dataset.inventoryMessage;
      const shortInventoryMessage = optionElement.dataset.shortInventoryMessage;

      if (paypalExcludeFlag) {
        formData.append('properties[_paypal_exclude_flag]', paypalExcludeFlag);
      }

      if (itemRestrictType) {
        formData.append('properties[_item_restrict_type]', itemRestrictType);
      }

      if (amazonPayExcludeFlag) {
        formData.append('properties[_amazon_pay_exclude_flag]', amazonPayExcludeFlag);
      }

      if (inventoryMessage) {
        formData.append('properties[_inventory_message]', inventoryMessage);
      }

      if (shortInventoryMessage) {
        formData.append('properties[_short_inventory_message]', shortInventoryMessage);
      }

      formData.append('quantity', finalQuantity.toString());

      const response = await fetch(`${Theme.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'text/html',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const data = await response.json();

      if (data.status) {
        throw new Error(data.message || 'Failed to add item to cart');
      }

      // Dispatch CartAddEvent for theme compatibility (same as product-form.js)
      if (data.sections) {
        const cartAddEvent = new CartAddEvent(data, variantId, {
          source: 'content-card-add-to-cart',
          itemCount: finalQuantity,
          productId: productId,
          sku: data.sku || null,
          sections: data.sections,
        });
        document.dispatchEvent(cartAddEvent);
      }

      this.setSuccessState(optionElement);
    } catch (error) {
      console.error('Error adding to cart:', error);

      const cartErrorEvent = new CustomEvent(ThemeEvents.cartError, {
        detail: {
          sourceId: 'content-card-add-to-cart',
          data: {
            message: error.message,
          },
        },
      });
      document.dispatchEvent(cartErrorEvent);

      this.setErrorState(optionElement, error.message);
    } finally {
      setTimeout(() => {
        this.setLoadingState(optionElement, false);
      }, 2000);
    }
  }

  /**
   * Set loading state for option element
   * @param {HTMLElement} optionElement
   * @param {boolean} isLoading
   */
  setLoadingState(optionElement, isLoading) {
    const priceElement = optionElement.querySelector('.content-card__product-price');
    const labelElement = optionElement.querySelector('.content-card__product-label');

    if (isLoading) {
      optionElement.classList.add('loading');
      if (labelElement) {
        if (!this.defaultLabels.has(optionElement)) {
          this.defaultLabels.set(optionElement, labelElement.textContent?.trim() || 'Add to cart');
        }
        labelElement.textContent = 'Adding...';
      }
      optionElement.style.pointerEvents = 'none';
    } else {
      optionElement.classList.remove('loading');
      // Success and error states reset the label on their own timers. Only undo "Adding..." here;
      // resetting unconditionally cut the error message short and made screen readers announce
      // "Add to cart" twice.
      if (labelElement && labelElement.textContent === 'Adding...') {
        labelElement.textContent = this.defaultLabels.get(optionElement) || 'Add to cart';
      }
      optionElement.style.pointerEvents = 'auto';
    }
  }

  /**
   * Set success state for option element
   * @param {HTMLElement} optionElement
   */
  setSuccessState(optionElement) {
    const labelElement = optionElement.querySelector('.content-card__product-label');
    if (labelElement) {
      labelElement.textContent = 'Added!';
    }
    optionElement.classList.add('success');

    setTimeout(() => {
      optionElement.classList.remove('success');
      if (labelElement) {
        labelElement.textContent = this.defaultLabels.get(optionElement) || 'Add to cart';
      }
    }, 2000);
  }

  /**
   * Set error state for option element
   * @param {HTMLElement} optionElement
   * @param {string} errorMessage
   */
  setErrorState(optionElement, errorMessage) {
    const labelElement = optionElement.querySelector('.content-card__product-label');
    if (labelElement) {
      labelElement.textContent = 'Error';
    }
    optionElement.classList.add('error');

    setTimeout(() => {
      optionElement.classList.remove('error');
      if (labelElement) {
        labelElement.textContent = this.defaultLabels.get(optionElement) || 'Add to cart';
      }
    }, 3000);
  }
}

new ContentCardAddToCart();
